import * as React from 'react'
import BackendContext from './backend-context'
import * as firebase from 'firebase/app'
import { getFirebase } from './get-firebase-instance'
import JobOffering from '../../types/jobOffering'
import { addDoc, arrayRemove, arrayUnion, collection, deleteDoc, doc, DocumentData, getFirestore, onSnapshot, QueryDocumentSnapshot, setDoc, updateDoc, writeBatch } from 'firebase/firestore'
import { listAll, getStorage, ref, uploadBytes, deleteObject, getBlob } from 'firebase/storage'
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth'

const offeringsCollectionName = "jobOfferings"
const documentsBucketName = "documents"
const datenschutzName = "datenschutzerklaerung.pdf"

const jobOfferFromDoc = (x: QueryDocumentSnapshot<DocumentData>) => ({
    id: x.id,
    name: x.data().name,
    aufgaben: x.data().aufgaben,
    anforderungen: x.data().anforderungen,
    dasErwartenWir: x.data().dasErwartenWir,
    dasBietenWir: x.data().dasBietenWir,
    maxHeightLarge: x.data().maxHeightLarge,
    maxHeightTablet: x.data().maxHeightTablet,
    maxHeightPhone: x.data().maxHeightPhone
})

const emptyPromise: Promise<void> = new Promise(() => { })

const BackendProvider: React.FC = ({ children }) => {
    const [firebase, setFirebase] = React.useState<firebase.FirebaseApp | null>(null)
    const [documents, setDocuments] = React.useState<string[]>([])
    const [documentsVersion, setDocumentsVersion] = React.useState(0)
    const [jobOfferings, setJobOfferings] = React.useState<JobOffering[]>([])
    const initSignedIn = typeof firebase === 'object' && firebase !== null && getAuth(firebase).currentUser !== null
    const [signedIn, setSignedIn] = React.useState<boolean>(initSignedIn)

    React.useEffect(() => {
        const firebaseInstance = getFirebase()
        setFirebase(firebaseInstance)
    }, [])
    // Offerings listener
    React.useEffect(() => {
        if (typeof firebase === 'object' && firebase) {
            const db = getFirestore(firebase)
            const unsubscribe = onSnapshot(collection(db, offeringsCollectionName),
                snaphot => {
                    console.log("Hello")
                    console.log(snaphot.docs)
                    setJobOfferings(snaphot.docs.map(jobOfferFromDoc))
                    console.log(jobOfferings)
                },
                err => {
                    setJobOfferings([])
                    console.error(err)
                })
            return unsubscribe
        }
    }, [firebase])
    // Documents "listener"
    React.useEffect(() => {
        if (typeof firebase === 'object' && firebase) {
            const storage = getStorage(firebase)
            const docsRef = ref(storage, documentsBucketName)
            listAll(docsRef).then(res => {
                console.log("Hello" + res)
                setDocuments(res.items.map(x => x.name))
            }).catch(err => {
                setDocuments([])
                console.error(err)
            })
        }
    }, [firebase, documentsVersion])

    const addDocument = (name, file) => {
        if (typeof firebase === 'object' && firebase) {
            const storage = getStorage(firebase)
            const docRef = ref(storage, `${documentsBucketName}/${name}`)
            return uploadBytes(docRef, file).then(() => {
                setDocumentsVersion(documentsVersion + 1)
            })
        }
        else return emptyPromise
    }

    const deleteDocument = (name) => {
        if (typeof firebase === 'object' && firebase) {
            const storage = getStorage(firebase)
            const docRef = ref(storage, `${documentsBucketName}/${name}`)
            return deleteObject(docRef).then(() => setDocumentsVersion(documentsVersion + 1))
        }
        else return emptyPromise
    }

    const downloadDocument = name => {
        if (typeof firebase === 'object' && firebase) {
            const storage = getStorage(firebase)
            const docRef = ref(storage, `${documentsBucketName}/${name}`)
            return getBlob(docRef)
        }
        else return emptyPromise.then(_ => null)
    }

    const addJobOffering = (offering) => {
        if (typeof firebase === 'object' && firebase) {
            const db = getFirestore(firebase)
            return addDoc(collection(db, offeringsCollectionName), offering).then(() => { })
        }
        else return emptyPromise
    }

    const editJobOffering = (offering, removedOffering) => {
        if (typeof firebase === 'object' && firebase && typeof offering.id === 'string' && offering.id) {
            const db = getFirestore(firebase)
            const batch = writeBatch(db)

            const docRef = doc(db, offeringsCollectionName, offering.id)
            batch.update(docRef, {
                aufgaben: arrayUnion(...offering.aufgaben),
                anforderungen: arrayUnion(...offering.anforderungen),
                dasErwartenWir: arrayUnion(...offering.dasErwartenWir),
                dasBietenWir: arrayUnion(...offering.dasBietenWir)
            })
            batch.update(docRef, {
                aufgaben: arrayRemove(...removedOffering.aufgaben),
                anforderungen: arrayRemove(...removedOffering.anforderungen),
                dasErwartenWir: arrayRemove(...removedOffering.dasErwartenWir),
                dasBietenWir: arrayRemove(...removedOffering.dasBietenWir)
            })
            batch.update(docRef, {
                maxHeightLarge: offering.maxHeightLarge,
                maxHeightTablet: offering.maxHeightTablet,
                maxHeightPhone: offering.maxHeightPhone
            })
            return batch.commit()
        }
        else return emptyPromise
    }

    const deleteJobOffering = (id) => {
        if (typeof firebase === 'object' && firebase) {
            const db = getFirestore(firebase)
            return deleteDoc(doc(db, offeringsCollectionName, id))
        }
        else return emptyPromise
    }

    const login = (email, password, onAuthStateChanged) => {
        if (typeof firebase === 'object' && firebase) {
            const auth = getAuth(firebase)
            return signInWithEmailAndPassword(auth, email, password)
                .then(() => {
                    auth.onAuthStateChanged(user => {
                        setSignedIn(user !== null)
                        onAuthStateChanged(user)
                    })
                })
        }
        else return emptyPromise
    }

    const getDatenschutz = () => {
        if (typeof firebase === 'object' && firebase) {
            const storage = getStorage(firebase)
            const docRef = ref(storage, datenschutzName)
            return getBlob(docRef)
        }
        else return emptyPromise.then(() => null)
    }

    return (<BackendContext.Provider
        value={{
            documents: documents,
            addDocument: addDocument,
            deleteDocument: deleteDocument,
            downloadDocument: downloadDocument,
            jobOfferings: jobOfferings,
            addJobOffering: addJobOffering,
            editJobOffering: editJobOffering,
            deleteJobOffering: deleteJobOffering,
            login: login,
            signedIn: signedIn,
            getDatenschutz: getDatenschutz
        }}>
        {children}
    </BackendContext.Provider >)
}

export default BackendProvider
